import {
  ArrowUpRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleDot,
  Database,
  Filter,
  GitBranch,
  Landmark,
  Link2,
  MapPin,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createUnavailableNationalProjectsSnapshot,
  fetchNationalProjects,
  NationalProjectsApiError,
  type NationalProjectEvidenceKind,
  type NationalProjectsSnapshot,
  type PublicNationalProject,
} from '@/lib/nationalProjects';
import styles from './NationalProjects.module.css';

type NationalProjectsProps = {
  mode?: 'live' | 'sample';
  initialSnapshot?: NationalProjectsSnapshot;
};

const formatNumber = (value: number) => new Intl.NumberFormat('en-GB').format(value);

const formatDate = (value: string | null) => {
  if (!value) return 'Date not reported';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
};

const formatConfidence = (value: number | null) => {
  if (value === null) return 'Not scored';
  return `${Math.round(value <= 1 ? value * 100 : value)}% confidence`;
};

const evidenceLabel = (kind: NationalProjectEvidenceKind) => {
  if (kind === 'source') return 'Source-backed';
  if (kind === 'inference') return 'Inference';
  if (kind === 'mixed') return 'Evidence + inference';
  return 'Evidence not classified';
};

const projectAnchor = (projectKey: string) => `project-${projectKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

function EvidenceBadge({ kind }: { kind: NationalProjectEvidenceKind }) {
  return (
    <span className={`${styles.evidenceBadge} ${styles[`evidence_${kind}`]}`}>
      {kind === 'source' ? <ShieldCheck size={12} /> : kind === 'inference' ? <GitBranch size={12} /> : <CircleDot size={12} />}
      {evidenceLabel(kind)}
    </span>
  );
}

function ProjectDetail({ project }: { project: PublicNationalProject }) {
  return (
    <details className={styles.projectDetail}>
      <summary>
        <span>Open evidence dossier</span>
        <span>{project.applications.length} records shown · {project.sources.length} sources</span>
        <ChevronDown size={16} aria-hidden="true" />
      </summary>
      <div className={styles.detailBody}>
        <section className={styles.timelineColumn} aria-label={`${project.title} update timeline`}>
          <div className={styles.detailHeading}><CalendarDays size={15} /><h4>Update timeline</h4></div>
          {project.timeline.length ? (
            <ol className={styles.timeline}>
              {project.timeline.map((event) => (
                <li key={event.event_key}>
                  <time>{formatDate(event.at)}</time>
                  <strong>{event.title}</strong>
                  <p>{event.detail}</p>
                  <EvidenceBadge kind={event.evidence_kind} />
                  {event.source_url ? <a href={event.source_url}>Open source <ArrowUpRight size={11} /></a> : null}
                </li>
              ))}
            </ol>
          ) : <p className={styles.detailEmpty}>No dated update timeline was returned.</p>}
        </section>

        <section className={styles.applicationColumn} aria-label={`${project.title} linked applications`}>
          <div className={styles.detailHeading}><Link2 size={15} /><h4>Linked applications</h4></div>
          {project.applications.length ? (
            <div className={styles.applicationList}>
              {project.applications.map((application) => (
                <article key={`${project.project_key}-${application.application_uid || application.reference_no}`}>
                  <div><span>{application.council}</span><span>{application.reference_no}</span></div>
                  <h5>{application.title}</h5>
                  {application.address ? <p><MapPin size={11} /> {application.address}</p> : null}
                  <footer>
                    <EvidenceBadge kind={application.relationship} />
                    <span>{application.status}</span>
                    {application.source_url ? <a href={application.source_url}>Application <ArrowUpRight size={10} /></a> : null}
                  </footer>
                </article>
              ))}
            </div>
          ) : <p className={styles.detailEmpty}>Application-level rows are not available in this response.</p>}
        </section>

        <section className={styles.sourceColumn} aria-label={`${project.title} sources`}>
          <div className={styles.detailHeading}><Database size={15} /><h4>Source register</h4></div>
          {project.sources.length ? (
            <ul className={styles.sourceList}>
              {project.sources.map((source) => (
                <li key={source.source_key}>
                  <EvidenceBadge kind={source.evidence_kind} />
                  <strong>{source.label}</strong>
                  <span>{source.publisher || 'Publisher not reported'}</span>
                  <small>Checked {formatDate(source.checked_at)}</small>
                  {source.url ? <a href={source.url}>Visit source <ArrowUpRight size={10} /></a> : null}
                </li>
              ))}
            </ul>
          ) : <p className={styles.detailEmpty}>No standalone source register was returned for this project.</p>}
        </section>
      </div>
    </details>
  );
}

export default function NationalProjects({ mode = 'live', initialSnapshot }: NationalProjectsProps) {
  const [snapshot, setSnapshot] = useState(
    initialSnapshot ?? createUnavailableNationalProjectsSnapshot(),
  );
  const [loading, setLoading] = useState(mode === 'live' && !initialSnapshot);
  const [error, setError] = useState<NationalProjectsApiError | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [council, setCouncil] = useState('all');
  const [evidence, setEvidence] = useState('all');

  const load = useCallback(async (signal?: AbortSignal) => {
    if (mode === 'sample') return;
    setLoading(true);
    try {
      const next = await fetchNationalProjects({ signal });
      setSnapshot(next);
      setError(null);
    } catch (caught) {
      const normalized = caught instanceof NationalProjectsApiError
        ? caught
        : new NationalProjectsApiError('The national projects register could not be loaded.');
      setError(normalized);
      setSnapshot((current) => current.available
        ? current
        : createUnavailableNationalProjectsSnapshot(normalized.message));
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    if (mode === 'sample' || initialSnapshot?.available) return undefined;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [initialSnapshot?.available, load, mode]);

  const facets = useMemo(() => ({
    statuses: [...new Set(snapshot.projects.map((project) => project.status))].sort(),
    priorities: [...new Set(snapshot.projects.map((project) => project.priority))].sort(),
    councils: [...new Set(snapshot.projects.flatMap((project) => project.councils.map((item) => item.name)))].sort(),
  }), [snapshot.projects]);

  const filteredProjects = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return snapshot.projects.filter((project) => {
      if (status !== 'all' && project.status !== status) return false;
      if (priority !== 'all' && project.priority !== priority) return false;
      if (council !== 'all' && !project.councils.some((item) => item.name === council)) return false;
      if (evidence !== 'all' && project.evidence_kind !== evidence) return false;
      if (!needle) return true;
      return [project.title, project.summary, project.category, project.tags.join(' '), project.councils.map((item) => item.name).join(' ')]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [council, evidence, priority, query, snapshot.projects, status]);
  const leadProject = snapshot.projects[0] ?? null;
  const leadRecord = leadProject?.applications[0] ?? null;
  const leadEvidencePercent = leadProject && leadProject.application_count > 0
    ? Math.min(100, Math.max(0, Math.round((leadProject.evidence_count / leadProject.application_count) * 100)))
    : 0;

  const filtersActive = query || status !== 'all' || priority !== 'all' || council !== 'all' || evidence !== 'all';
  const clearFilters = () => {
    setQuery('');
    setStatus('all');
    setPriority('all');
    setCouncil('all');
    setEvidence('all');
  };

  return (
    <div className={styles.root} data-view-mode={mode}>
      {mode === 'sample' ? (
        <div className={styles.sampleBanner} role="status">
          <Sparkles size={14} /> Local sample · illustrative projects · not live
        </div>
      ) : null}
      <header className={styles.hero}>
        <div className={styles.heroRule}><span>CityScope register / Scotland</span><span>Evidence first · updated {formatDate(snapshot.generated_at)}</span></div>
        <div className={styles.heroGrid}>
          <div>
            <p className={styles.eyebrow}>National projects</p>
            <h1>The developments that<br /><em>do not stop at council borders.</em></h1>
          </div>
          <div className={styles.heroAside}>
            <p>
              CityScope joins planning records into larger, evolving projects — while keeping direct evidence visibly separate from inference.
            </p>
            <div className={styles.legend}>
              <EvidenceBadge kind="source" />
              <EvidenceBadge kind="inference" />
              <EvidenceBadge kind="mixed" />
            </div>
          </div>
        </div>
      </header>

      <main>
        {loading ? (
          <div className={styles.statePanel} role="status">
            <RefreshCw className={styles.spin} size={20} />
            <div><strong>Assembling the national register</strong><span>Loading projects and their evidence links…</span></div>
          </div>
        ) : null}
        {error ? (
          <div className={`${styles.statePanel} ${styles.errorPanel}`} role="alert">
            <TriangleAlert size={20} />
            <div><strong>We could not load the live register</strong><span>{error.message} No sample records have been substituted.</span></div>
            <button type="button" onClick={() => void load()}>Try again</button>
          </div>
        ) : null}
        {!loading && !error && !snapshot.available ? (
          <div className={styles.statePanel} role="status">
            <Database size={20} />
            <div><strong>The register is not available yet</strong><span>{snapshot.unavailable_reason} No project is inferred from an empty response.</span></div>
          </div>
        ) : null}

        <section className={styles.coverageStrip} aria-label="National projects coverage">
          <div><span>Projects tracked</span><strong>{formatNumber(snapshot.coverage.project_count)}</strong><small>current public dossiers</small></div>
          <div><span>Planning links</span><strong>{formatNumber(snapshot.coverage.linked_application_count)}</strong><small>{formatNumber(snapshot.coverage.source_backed_link_count)} source-backed</small></div>
          <div><span>Council reach</span><strong>{snapshot.coverage.councils_represented}<i> / {snapshot.coverage.councils_expected || '—'}</i></strong><small>represented / expected</small></div>
          <div><span>Inference held apart</span><strong>{formatNumber(snapshot.coverage.inferred_link_count)}</strong><small>links needing confirmation</small></div>
        </section>

        {!loading && snapshot.available && leadProject ? (
          <section className={styles.leadBriefing} aria-labelledby="lead-briefing-title">
            <div className={styles.leadBriefingCopy}>
              <div className={styles.leadRule}><span>Lead current dossier</span><span>{leadProject.priority} priority · {formatDate(leadProject.updated_at)}</span></div>
              <p className={styles.eyebrow}>The national read</p>
              <h2 id="lead-briefing-title">{leadProject.title}</h2>
              <p className={styles.leadStandfirst}>{leadProject.summary}</p>
              <div className={styles.editorialRationale}>
                <span>Why CityScope groups it</span>
                <p>{leadProject.evidence_summary || 'No source-bound rationale was supplied for this current dossier.'}</p>
              </div>
              <a href={`#${projectAnchor(leadProject.project_key)}`}>Read the evidence dossier <ArrowUpRight size={13} /></a>
            </div>
            <aside className={styles.leadBriefingFacts} aria-label={`${leadProject.title} source briefing`}>
              <div className={styles.leadEvidenceTop}>
                <EvidenceBadge kind={leadProject.evidence_kind} />
                <strong>{formatConfidence(leadProject.confidence)}</strong>
              </div>
              <div className={styles.coverageMeter}>
                <div><span>Direct evidence coverage</span><strong>{leadProject.evidence_count} / {leadProject.application_count}</strong></div>
                <div aria-hidden="true"><span style={{ width: `${leadEvidencePercent}%` }} /></div>
              </div>
              <dl className={styles.leadNumbers}>
                <div><dt>Council footprint</dt><dd>{leadProject.council_count}</dd></div>
                <div><dt>Linked records</dt><dd>{leadProject.application_count}</dd></div>
                <div><dt>Inference links</dt><dd>{leadProject.inference_count}</dd></div>
              </dl>
              <div className={styles.placeLine}>
                <span>Places in the dossier</span>
                <p>{leadProject.councils.length ? leadProject.councils.map((item) => item.name).join(' · ') : 'No councils reported'}</p>
              </div>
              {leadRecord ? (
                <div className={styles.sourceSignal}>
                  <span>Linked source signal</span>
                  <strong>{leadRecord.title || leadRecord.reference_no}</strong>
                  <p>{leadRecord.reference_no} · {leadRecord.council}{leadRecord.status ? ` · ${leadRecord.status}` : ''}</p>
                  {leadRecord.source_url ? <a href={leadRecord.source_url}>Open record <ArrowUpRight size={11} /></a> : null}
                </div>
              ) : null}
            </aside>
          </section>
        ) : null}

        <section className={styles.register} aria-labelledby="register-heading">
          <div className={styles.registerHeading}>
            <div><p className={styles.eyebrow}>The live register</p><h2 id="register-heading">Projects in view</h2></div>
            <p>{filteredProjects.length} of {snapshot.projects.length} projects match this view.</p>
          </div>

          <div className={styles.filters} aria-label="Filter national projects">
            <label className={styles.searchField}>
              <span>Search projects</span>
              <div><Search size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Project, council, theme…" /></div>
            </label>
            <label><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">All statuses</option>{facets.statuses.map((item) => <option key={item}>{item}</option>)}</select></label>
            <label><span>Priority</span><select value={priority} onChange={(event) => setPriority(event.target.value)}><option value="all">All priorities</option>{facets.priorities.map((item) => <option key={item}>{item}</option>)}</select></label>
            <label><span>Council</span><select value={council} onChange={(event) => setCouncil(event.target.value)}><option value="all">All councils</option>{facets.councils.map((item) => <option key={item}>{item}</option>)}</select></label>
            <label><span>Evidence</span><select value={evidence} onChange={(event) => setEvidence(event.target.value)}><option value="all">All evidence</option><option value="source">Source-backed</option><option value="mixed">Evidence + inference</option><option value="inference">Inference</option><option value="unknown">Unclassified</option></select></label>
            {filtersActive ? <button type="button" className={styles.clearButton} onClick={clearFilters}><Filter size={13} /> Clear</button> : null}
          </div>

          {!loading && filteredProjects.length ? (
            <div className={styles.projectList}>
              {filteredProjects.map((project, index) => (
                <article className={styles.project} id={projectAnchor(project.project_key)} key={project.project_key}>
                  <header className={styles.projectHeader}>
                    <div className={styles.projectIndex}>{String(index + 1).padStart(2, '0')}</div>
                    <div className={styles.projectTitle}>
                      <div className={styles.projectMeta}><span>{project.category}</span><span>{project.priority} priority</span><span>{project.status}</span><span>{project.source_state} sources</span><span>{project.review_state}</span></div>
                      <h3>{project.title}</h3>
                      <p>{project.summary}</p>
                    </div>
                    <div className={styles.projectEvidenceSummary}>
                      <EvidenceBadge kind={project.evidence_kind} />
                      <strong>{formatConfidence(project.confidence)}</strong>
                      <p>{project.evidence_summary || 'No evidence summary was supplied.'}</p>
                      <dl className={styles.evidenceLabels}>
                        <div><dt>Summary</dt><dd>{project.evidence_labels.summary.replaceAll('_', ' ')}</dd></div>
                        <div><dt>Claims</dt><dd>{project.evidence_labels.claims.replaceAll('_', ' ')}</dd></div>
                        <div><dt>Applications</dt><dd>{project.evidence_labels.applications.replaceAll('_', ' ')}</dd></div>
                      </dl>
                    </div>
                  </header>

                  <div className={styles.projectFacts}>
                    <div><Building2 size={14} /><strong>{project.application_count}</strong><span>linked applications</span></div>
                    <div><Landmark size={14} /><strong>{project.council_count}</strong><span>council areas</span></div>
                    <div><CheckCircle2 size={14} /><strong>{project.evidence_count}</strong><span>source-backed links</span></div>
                    <div><GitBranch size={14} /><strong>{project.inference_count}</strong><span>inferred links</span></div>
                    <div><CalendarDays size={14} /><strong>{formatDate(project.updated_at)}</strong><span>latest update</span></div>
                  </div>

                  <div className={styles.councilRail}>
                    <span>Councils</span>
                    <div>{project.councils.length ? project.councils.map((item) => <span key={item.name}>{item.name}<b>{item.application_count || ''}</b></span>) : <em>Not reported</em>}</div>
                  </div>

                  <ProjectDetail project={project} />
                </article>
              ))}
            </div>
          ) : null}

          {!loading && snapshot.projects.length === 0 ? (
            <div className={styles.emptyState}>
              <Database size={24} />
              <h3>No national projects are published yet</h3>
              <p>This is an honest empty register. CityScope will not promote private proposals or fabricate a public list.</p>
            </div>
          ) : null}

          {!loading && snapshot.projects.length > 0 && filteredProjects.length === 0 ? (
            <div className={styles.emptyState}>
              <Search size={24} />
              <h3>No projects match these filters</h3>
              <p>Try widening the status, council or evidence view.</p>
              <button type="button" onClick={clearFilters}>Clear all filters</button>
            </div>
          ) : null}
        </section>
      </main>

      <footer className={styles.footer}>
        <div><ShieldCheck size={15} /> CityScope evidence standard</div>
        <p>Evidence identifies a direct source relationship. Inference is a CityScope interpretation awaiting or supplementing primary confirmation.</p>
        <a href="/planning-updates">Explore all planning applications <ArrowUpRight size={12} /></a>
      </footer>
    </div>
  );
}
